/* eslint-disable max-len */
import * as XLSX from 'xlsx-js-style';
import { getPreparedData, getPreparedDataByPeriod } from './mapping';
import { fitToColumn } from '../utils';
import { withHeaderStyling } from '../../../utils';
import { GeneralLedgerTransaction } from 'src/app/domain/financial-statement/models/common/GeneralLedgerTransaction';
import { ExcelOptions, ExcelTemplate, Rows } from '../../../interfaces';
import { SortService } from 'src/app/domain/company/service/sort.service';

export class GeneralLedgerTemplate implements ExcelTemplate {
  headers = [
    'Date',
    'Account number',
    'Account name',
    'Debit',
    'Credit',
    'Contra account number',
    'Contra account name',
    'Description',
    'Journal entry',
    'isManual',
    'Comment',
  ];

  export(exportExcelData: GeneralLedgerTransaction[] | Map<string, GeneralLedgerTransaction[]> | Map<string, GeneralLedgerTransaction[]>[], options?: ExcelOptions): XLSX.WorkBook {
    const book = XLSX.utils.book_new();
    const sheet = this.getSheet(exportExcelData, options);
    XLSX.utils.book_append_sheet(book, sheet, this.getSheetName(exportExcelData));
    return book;
  }


  getSheet(exportExcelData: GeneralLedgerTransaction[] | Map<string, GeneralLedgerTransaction[]> | Map<string, GeneralLedgerTransaction[]>[], options?: ExcelOptions): XLSX.WorkSheet {
    let preparedData: Rows;
    let exportExcelDataTyped = exportExcelData;
    if (Array.isArray(exportExcelData) && !(exportExcelData[0] instanceof GeneralLedgerTransaction)) {
      exportExcelDataTyped = this.consolidateArrayOfMaps(exportExcelData as Map<string, GeneralLedgerTransaction[]>[]);
    }
    if (options && options.isQuarterlyGl && !Array.isArray(exportExcelDataTyped)) {
      exportExcelDataTyped = SortService.sortGlMap(exportExcelDataTyped);
      preparedData = getPreparedDataByPeriod(exportExcelDataTyped, this.headers);
    } else if (Array.isArray(exportExcelDataTyped)) {
      SortService.sortGlTransactions(exportExcelDataTyped as GeneralLedgerTransaction[]);
      preparedData = getPreparedData(exportExcelDataTyped as GeneralLedgerTransaction[], this.headers);
    }
    const sheet = {
      ...XLSX.utils.aoa_to_sheet(preparedData),
      '!cols': fitToColumn(preparedData),
    };
    return withHeaderStyling(sheet);
  }

  private getSheetName(exportExcelData: GeneralLedgerTransaction[] | Map<string, GeneralLedgerTransaction[]> | Map<string, GeneralLedgerTransaction[]>[]): string {
    const allTransactions = this.combineTransactions(exportExcelData);

    if (allTransactions.length && this.isSingleAccount(allTransactions)) {
      const account = allTransactions[0].account;
      const sheetName = `${account.code} - ${account.name}`;
      return sheetName.length > 30 ? `${sheetName.substring(0, 27)}...` : sheetName;
    }
    return 'GL';
  }

  private isSingleAccount(transactions: GeneralLedgerTransaction[]): boolean {
    const firstAccountName = transactions[0].account.code;
    return transactions.every((transaction) => transaction.account.code === firstAccountName);
  }

  private combineTransactions(exportExcelData: GeneralLedgerTransaction[] | Map<string, GeneralLedgerTransaction[]> | Map<string, GeneralLedgerTransaction[]>[]): GeneralLedgerTransaction[] {
    if (Array.isArray(exportExcelData)) {
      return exportExcelData.flatMap((item) =>
        (item instanceof Map ? Array.from(item.values()).flat() : item),
      );
    } if (exportExcelData instanceof Map) {
      let transactions: GeneralLedgerTransaction[] = [];
      exportExcelData.forEach((value) => {
        transactions = transactions.concat(this.combineTransactions(value));
      });
      return transactions;
    }
    return [];
  }

  private consolidateArrayOfMaps(mapArr: Map<string, GeneralLedgerTransaction[]>[]): Map<string, GeneralLedgerTransaction[]> {
    const consolidatedMap = new Map<string, GeneralLedgerTransaction[]>();
    for (const map of mapArr) {
      map.forEach((value, key) => {
        if (!consolidatedMap.has(key)) {
          consolidatedMap.set(key, []);
        }
        const existingValue = consolidatedMap.get(key) || [];
        consolidatedMap.set(key, [...existingValue, ...value]);
      });
    }
    return consolidatedMap;
  }

}
